import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { DatePicker } from "@/components/date-picker";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { StageCombobox } from "@/features/stages/components/stage-combobox";
import { dateOnlyToDate, dateToDateOnly } from "@/lib/format.ts";
import { useDeleteSchedule } from "../../hooks/use-delete-schedule.ts";
import { useSetSchedule } from "../../hooks/use-set-schedule.ts";
import {
	type ScheduleFormValues,
	scheduleSchema,
} from "../../schemas/schedule-schema.ts";
import type { ScheduleNode } from "../../types.ts";
import { childrenCount } from "../../utils/tree.ts";

export interface ScheduleDraft {
	stageId?: string;
	start?: string;
	end?: string;
}

const VIOLATION_FIELDS: Record<string, keyof ScheduleFormValues> = {
	stage_id: "stageId",
	planned_start_date: "startDate",
	planned_end_date: "endDate",
};

const DESCRIPTION_MESSAGES: Record<string, string> = {
	required: "Campo obrigatório.",
	invalid: "Data inválida.",
	"gte:planned_start_date":
		"A data de término deve ser igual ou posterior ao início.",
	not_leaf: "Somente etapas sem sub-etapas podem ter cronograma.",
};

function describe(description: string): string {
	return DESCRIPTION_MESSAGES[description] ?? description;
}

export function ScheduleEditor({
	open,
	onOpenChange,
	constructionId,
	nodes,
	draft,
	onPreview,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionId: string;
	nodes: ScheduleNode[];
	draft: ScheduleDraft | null;
	onPreview?: (draft: ScheduleDraft | null) => void;
}) {
	const [formError, setFormError] = useState<string | null>(null);
	const setSchedule = useSetSchedule(constructionId);
	const deleteSchedule = useDeleteSchedule(constructionId);
	const isPending = setSchedule.isPending || deleteSchedule.isPending;

	const branchIds = useMemo(
		() => new Set(childrenCount(nodes).keys()),
		[nodes],
	);

	const {
		control,
		handleSubmit,
		reset,
		setError,
		watch,
		formState: { errors },
	} = useForm<ScheduleFormValues>({
		resolver: zodResolver(scheduleSchema),
		defaultValues: { stageId: "", startDate: "", endDate: "" },
	});

	useEffect(() => {
		if (open) {
			reset({
				stageId: draft?.stageId ?? "",
				startDate: draft?.start ?? "",
				endDate: draft?.end ?? "",
			});
			setFormError(null);
		}
	}, [open, draft, reset]);

	const startValue = watch("startDate");
	const endValue = watch("endDate");
	const minEndDate = dateOnlyToDate(startValue);

	const selectedStageId = watch("stageId");
	const selectedNode = nodes.find((node) => node.id === selectedStageId);
	const hasSchedule = !!selectedNode?.ownStart && !!selectedNode?.ownEnd;

	useEffect(() => {
		if (!onPreview) return;
		if (!open) {
			onPreview(null);
			return;
		}
		onPreview({ stageId: selectedStageId, start: startValue, end: endValue });
	}, [onPreview, open, selectedStageId, startValue, endValue]);

	useEffect(() => () => onPreview?.(null), [onPreview]);

	function applyAPIError(err: unknown, fallback: string) {
		if (err instanceof APIError) {
			let matchedField = false;
			for (const [field, description] of Object.entries(err.violations)) {
				const formField = VIOLATION_FIELDS[field];
				if (formField) {
					setError(formField, { message: describe(description) });
					matchedField = true;
				} else {
					setFormError(describe(description));
					matchedField = true;
				}
			}
			if (!matchedField) setFormError(err.message || fallback);
			return;
		}
		setFormError(err instanceof Error ? err.message : fallback);
	}

	async function onSubmit(values: ScheduleFormValues) {
		setFormError(null);
		try {
			await setSchedule.mutateAsync({
				stageId: values.stageId,
				input: {
					plannedStartDate: values.startDate,
					plannedEndDate: values.endDate,
				},
			});
			toast.success("Cronograma atualizado com sucesso!");
			onOpenChange(false);
		} catch (err) {
			applyAPIError(err, "Não foi possível salvar o cronograma.");
		}
	}

	async function handleClear() {
		if (!selectedNode || !hasSchedule) return;
		setFormError(null);
		try {
			await deleteSchedule.mutateAsync(selectedNode.id);
			toast.success("Cronograma removido.");
			onOpenChange(false);
		} catch (err) {
			applyAPIError(err, "Não foi possível remover o cronograma.");
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Datas planejadas</DialogTitle>
					<DialogDescription>
						Escolha a etapa e o período planejado. Somente etapas sem sub-etapas
						podem ser agendadas.
					</DialogDescription>
				</DialogHeader>
				<form
					id="schedule-form"
					className="schedule-editor"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<div className="form-group">
						<Label htmlFor="schedule-stage">Etapa</Label>
						<Controller
							control={control}
							name="stageId"
							render={({ field }) => (
								<StageCombobox
									id="schedule-stage"
									stages={nodes}
									value={field.value}
									onChange={field.onChange}
									excludeIds={branchIds}
									allowNone={false}
									placeholder="Selecione a etapa"
								/>
							)}
						/>
						{errors.stageId && (
							<p className="form-error">{errors.stageId.message}</p>
						)}
					</div>

					<div className="schedule-editor__dates">
						<div className="form-group">
							<Label htmlFor="schedule-start">Início planejado</Label>
							<Controller
								control={control}
								name="startDate"
								render={({ field }) => (
									<DatePicker
										id="schedule-start"
										name={field.name}
										modal
										selected={dateOnlyToDate(field.value)}
										onSelected={(date) => field.onChange(dateToDateOnly(date))}
										aria-invalid={!!errors.startDate}
									/>
								)}
							/>
							{errors.startDate && (
								<p className="form-error">{errors.startDate.message}</p>
							)}
						</div>
						<div className="form-group">
							<Label htmlFor="schedule-end">Término planejado</Label>
							<Controller
								control={control}
								name="endDate"
								render={({ field }) => (
									<DatePicker
										id="schedule-end"
										name={field.name}
										modal
										selected={dateOnlyToDate(field.value)}
										onSelected={(date) => field.onChange(dateToDateOnly(date))}
										aria-invalid={!!errors.endDate}
										calendarProps={{
											disabled: minEndDate ? { before: minEndDate } : undefined,
										}}
									/>
								)}
							/>
							{errors.endDate && (
								<p className="form-error">{errors.endDate.message}</p>
							)}
						</div>
					</div>

					{formError && (
						<p role="alert" className="form-error-alert">
							{formError}
						</p>
					)}
				</form>
				<DialogFooter className="schedule-editor__footer">
					{hasSchedule && (
						<Button
							type="button"
							variant="ghost"
							className="schedule-editor__clear"
							onClick={handleClear}
							disabled={isPending}
						>
							<Trash2 />
							Remover datas
						</Button>
					)}
					<div className="schedule-editor__footer-actions">
						<Button
							type="button"
							variant="ghost"
							onClick={() => onOpenChange(false)}
							disabled={isPending}
						>
							Cancelar
						</Button>
						<Button type="submit" form="schedule-form" disabled={isPending}>
							{isPending && <Loader2 className="schedule-editor__spinner" />}
							Salvar
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
